/**
 * Utility functions for generating reference IDs based on ForestSync patterns
 * Ported from generator.py
 */

import { customAlphabet } from "nanoid"
import { eq } from "drizzle-orm"

// Alphanumeric only — no underscore or hyphen (URL-safe, certificate-safe)
const nanoid = customAlphabet("ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789")

/**
 * Generate a unique user refId: slugified name (up to 6 chars) + random chars to make 8
 */
export function generateUserRefId(name: string): string {
  // Remove non-alphanumeric, split into words
  const words = name.toLowerCase().replace(/[^a-z0-9 ]/g, '').split(' ').filter(Boolean);
  let base = '';
  if (words.length === 1) {
    // For single word, take 2-3 chars from 2-3 random positions
    const w = words[0];
    const picks = [0, Math.floor(w.length / 3), Math.floor(2 * w.length / 3)];
    base = picks.map(i => w.slice(i, i + 2)).join('').slice(0, 6);
  } else {
    // For multi-word, take 2-3 chars from each word, up to 8 total
    base = words.map(w => w.slice(0, 3)).join('').slice(0, 8);
  }
  // If base is less than 8, pad with random nanoid chars
  if (base.length < 8) {
    base += nanoid(8 - base.length);
  }
  return base.slice(0, 8).toUpperCase();
}

/**
 * Generate a unique user refId with database uniqueness check
 * This is an async version that ensures the generated refId is unique in the database
 */
export async function generateUniqueUserRefId(name: string, ctx: { db: any; schema: any }): Promise<string> {
  let attempts = 0;
  const maxAttempts = 10;
  const usersTable = ctx.schema.users;

  while (attempts < maxAttempts) {
    const refId = generateUserRefId(name);

    try {
      // Check if refId already exists in database
      const existing = await ctx.db
        .select({ id: usersTable.id })
        .from(usersTable)
        .where(eq(usersTable.refId, refId))
        .limit(1);

      if (existing.length === 0) {
        return refId;
      }

      // If collision, append more random chars but keep total length at 8
      const randomSuffix = nanoid(4);
      const newRefId = (refId.slice(0, 4) + randomSuffix).toUpperCase();

      const existingNew = await ctx.db
        .select({ id: usersTable.id })
        .from(usersTable)
        .where(eq(usersTable.refId, newRefId))
        .limit(1);

      if (existingNew.length === 0) {
        return newRefId;
      }

      attempts++;
    } catch (error) {
      // If database check fails, fall back to original function
      console.warn('Failed to check refId uniqueness, using fallback:', error);
      return generateUserRefId(name);
    }
  }

  // If all attempts failed, generate a completely random refId
  return nanoid(8).toUpperCase();
}

/**
 * Generate a unique reference ID for carbon sinks
 * Format: [2-letter-country-code]-[4-char-random]
 * 
 * @param countryCode - Two letter country code
 * @returns A formatted string reference ID
 */
export function generateSinkRefId(countryCode: string): string {
  return `${countryCode.toUpperCase()}-${nanoid(4)}`
}

/**
 * Generate a unique reference ID for transactions
 * Format: FSTX[Year][Month][8-char-Random]
 * 
 * @returns A formatted string reference ID
 */
export function generateTransactionRefId(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = (now.getMonth() + 1).toString().padStart(2, "0")
  return `FSTX${year}${month}${nanoid(8)}`
}

/**
 * Generate a short readable reference ID for orders.
 * Format: {10 alphanumeric chars}
 */
export function generateOrderRefId(): string {
  return `${nanoid(10)}`
}

/**
 * Generate a unique reference ID for trade orders.
 * Format: FSOT[Year][Month][8-char-Random]
 * Example: FSOT202603abcd1234
 */
export function generateTradeOrderRefId(): string {
  const date = new Date()
  const ym = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`
  return `FSOT${ym}${nanoid(8)}`
}


/**
 * Generate a unique reference ID for events
 * Format: FSEVT[yyMM][sinkRefId][eventTypeCode][nanoid(4)]
 *
 * @param sinkRefId - The reference ID of the carbon sink
 * @param eventTypeCode - The event type code (number)
 * @param timestamp - The event timestamp
 * @returns A formatted string reference ID
 */
export function generateEventRefId(
  sinkRefId: string,
  eventTypeCode: number,
  timestamp: Date
): string {
  const yyyy = String(timestamp.getFullYear());
  const mm = String(timestamp.getMonth() + 1).padStart(2, "0");
  const cleanSinkRefId = sinkRefId.replace(/-/g, "");
  const eventTypeStr = String(eventTypeCode).padStart(3, "0");
  return `FSEVT${yyyy}${mm}${cleanSinkRefId}${eventTypeStr}${nanoid(8)}`;
}

/**
 * Generate a token serial number based on ForestSync format.
 * Format: FS[lifespan_3][CC][YYYY][MM][random_5]aa[sinkIdPart][seqIndex_8]
 * External registry links (Verra, Gold Standard, etc.) are stored separately
 * in the sink's details.externalRegistries — they do not affect this serial.
 *
 * Lifespan encoding (always 3 chars):
 *   < 1000yr  → zero-padded digits: 50 → "050", 100 → "100"
 *   ≥ 1000yr  → K-notation [thousands]K[hundreds]: 1000 → "1K0", 1500 → "1K5", 2000 → "2K0"
 */
export function generateTokenSerialNumber(
  lifespan: number,
  countryCode: string,
  sinkRefId: string,
  tokenSequenceIndex: number,
): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = (now.getMonth() + 1).toString().padStart(2, "0")
  const randomPart = nanoid(5)
  const prefix = "FS"

  const lifespanStr =
    lifespan < 1000
      ? lifespan.toString().padStart(3, "0")
      : `${Math.floor(lifespan / 1000)}K${Math.floor((lifespan % 1000) / 100)}`

  // Extract sink_ref_id without country code (after the dash)
  const sinkIdPart = sinkRefId.includes("-") ? sinkRefId.split("-")[1] : sinkRefId

  // Pad token sequence index to 8 digits
  const tokenIndexStr = tokenSequenceIndex.toString().padStart(8, "0")

  return `${prefix}${lifespanStr}${countryCode.toUpperCase()}${year}${month}${randomPart}aa${sinkIdPart}${tokenIndexStr}`
}

/**
 * Generate a unique reference ID for procurement mandates.
 * Format: FSPM[Year][Month][8-char-Random]
 */
export function generateProcurementMandateRefId(): string {
  const date = new Date()
  const ym = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`
  return `FSPM${ym}${nanoid(8)}`
}

/**
 * Generate a unique reference ID for broker option listings.
 * Format: FSOPT[Year][Month][8-char-Random]
 */
export function generateOptionListingRefId(): string {
  const date = new Date()
  const ym = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`
  return `FSOPT${ym}${nanoid(8)}`
}

/**
 * Generate a unique reference ID for broker option contracts.
 * Format: FSOPC[Year][Month][8-char-Random]
 */
export function generateOptionContractRefId(): string {
  const date = new Date()
  const ym = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`
  return `FSOPC${ym}${nanoid(8)}`
}

/**
 * Generate a unique reference ID for an external merchant record.
 * Format: FSMER[Year][Month][8-char-Random]
 */
export function generateMerchantRefId(): string {
  const date = new Date()
  const ym = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`
  return `FSMER${ym}${nanoid(8)}`
}

/**
 * Generate the public-facing serial for a single external-merchant allocation (one per retail
 * line — e.g. one garment). Distinct from generateTokenSerialNumber: that identifies the
 * underlying retired tonne itself, which many allocations can share; this identifies one
 * allocation against it, so a QR/URL always resolves to exactly one garment.
 * Format: FSALLOC[Year][Month][10-char-Random]
 */
export function generateAllocationSerial(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = (now.getMonth() + 1).toString().padStart(2, "0")
  return `FSALLOC${year}${month}${nanoid(10)}`
}

/**
 * Utility type for ref ID generators
 */
export type RefIdGenerator = {
  user: (name: string) => string
  sink: (countryCode: string) => string
  transaction: () => string
  tokenSerial: (lifespan: number, countryCode: string, sinkRefId: string, sequenceIndex: number) => string
}

/**
 * Centralized ref ID generator object
 */
export const refIdGenerator: RefIdGenerator = {
  user: generateUserRefId,
  sink: generateSinkRefId,
  transaction: generateTransactionRefId,
  tokenSerial: generateTokenSerialNumber
}
